import BaseDesignDataService from "@configuratorware/configurator-frontendgui/App/Services/DesignDataService";
import { Services } from "@configuratorware/configurator-frontendgui/App/ServiceLocator";
import { requestLock } from '@configuratorware/configurator-frontendgui/Utils/Async/ObjectLock';
import { isDesignerProductPreviewMode } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { designAreaAppliesMask, getVisualizationEffectWithOptions } from '@configuratorware/configurator-frontendgui/App/Reducers/DesignArea/Selectors';
import get from 'lodash/get';
import { getFontMapping } from "@configuratorware/configurator-frontendgui/App/Reducers/Fonts/Selectors";



class DesignDataService extends BaseDesignDataService {


    // needed to override the method because borderColor can not be set via config.
    async redrawAllDesignAreas(getDrawingContext, updateEventObject) {
        const state = Services.store.state;

        const showHighlight = get(state, 'designArea.showHighlight');

        const textureBuilderConfig = {
            highlight: {
                borderColor: '#ffffff',
                backgroundColor: 'rgba(255,255,255,0.1)',
            },
        };

        const variantIdentifier = get(updateEventObject, 'data.variant', false);

        const effectEnabled = !isDesignerProductPreviewMode();

        const drawingFn = async (textureCtx, getTextureBuilder, designAreas = this.getDesignAreas()) => {
            const {
                getTextureImage,
                drawCanvasToArea,
                highlightDesignAreas,
                drawOn,
                getParams,
            } = getTextureBuilder(textureBuilderConfig);

            if (!getTextureImage()) {
                // eslint-disable-next-line no-console
                console.warn('Texture image is missing!', designAreas);
                return;
            }

            if (showHighlight) {
                // highlight only the design areas with no mask
                // for the others the outline is added by the maskHandler
                highlightDesignAreas(
                    designAreas.filter(designArea => !designAreaAppliesMask(state, designArea))
                );
            }

            for (const designArea of designAreas) {
                const canvas = this._getCanvas(this.getDesignAreaByIdentifier(designArea.identifier));
                if (canvas) {
                    const releaseLock = await requestLock(canvas);

                    const selectedObject = get(canvas, 'selectedObject');
                    if (selectedObject) {
                        selectedObject.selected = false;
                    }

                    const maskHandler = canvas.getMaskHandler();
                    let restoreOutlineVisibility;
                    if (maskHandler) {
                        restoreOutlineVisibility = maskHandler.toggleOutline(showHighlight, {
                            getZoomFactor: ({zoom}) => {
                                // currentZoom is only available for 2D, for 3D it is always 1
                                const threeFactor = Math.min(getParams().currentZoom, 1) || 1;
                                return zoom * threeFactor;
                            },
                        });
                    }

                    const drawToContext = canvas.drawToContext.bind(canvas);
                    const getSvg = () => canvas.getScreenshot(getFontMapping(state));
                    await drawCanvasToArea({
                        drawToContext,
                        designArea,
                        getSvg,
                        ...(effectEnabled &&
                            getVisualizationEffectWithOptions(state, designArea, variantIdentifier)),
                    });
                    if (maskHandler) {
                        restoreOutlineVisibility();
                    }

                    releaseLock();
                }
            }

            drawOn(textureCtx);
        };

        try {
            await getDrawingContext(
                drawingFn,
                true /* update alpha */,
                true /* clean up texture before drawing */
            );
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Could not get drawing context, texture update is skipped!', e.message);
        }
    }

}

export default DesignDataService;